import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const EditSalaryDetail = ({ show, onHide, salaryDetail, employee, payComponents, onSalaryDetailUpdated }) => {
  const [formData, setFormData] = useState({
    salary_detail_id: '',
    component_id: '',
    component_type: 'Earning',
    calculation_type: 'Fixed',
    value: '',
    component_order: 1,
    is_active: 1
  });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [availableOrders, setAvailableOrders] = useState([1]);
  const [formulaSuggestions, setFormulaSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showFunctions, setShowFunctions] = useState(false); // New state for functions dropdown

  const API_BASE = process.env.REACT_APP_API_BASE;

  // Define allowed functions
  const allowedFunctions = [
    'MIN', 'MAX', 'ABS', 'CEIL', 'FLOOR', 'ROUND', 'SQRT', 'POW',
    'SUM', 'AVG', 'AVERAGE', 'IF', 'AND', 'OR', 'NOT', 'MOD',
    'EXP', 'LOG', 'LOG10', 'SIN', 'COS', 'TAN', 'ASIN', 'ACOS',
    'ATAN', 'DEG2RAD', 'RAD2DEG'
  ];

  // Function descriptions for tooltips
  const functionDescriptions = {
    'MIN': 'Returns the smallest value: MIN(value1, value2, ...)',
    'MAX': 'Returns the largest value: MAX(value1, value2, ...)',
    'ABS': 'Returns absolute value: ABS(number)',
    'CEIL': 'Rounds up to nearest integer: CEIL(number)',
    'FLOOR': 'Rounds down to nearest integer: FLOOR(number)',
    'ROUND': 'Rounds to specified decimals: ROUND(number, decimals)',
    'SQRT': 'Square root: SQRT(number)',
    'POW': 'Power: POW(base, exponent)',
    'SUM': 'Sum of values: SUM(value1, value2, ...)',
    'AVG': 'Average of values: AVG(value1, value2, ...)',
    'AVERAGE': 'Average of values: AVERAGE(value1, value2, ...)',
    'IF': 'Conditional: IF(condition, true_value, false_value)',
    'AND': 'Logical AND: AND(condition1, condition2, ...)',
    'OR': 'Logical OR: OR(condition1, condition2, ...)',
    'NOT': 'Logical NOT: NOT(condition)',
    'MOD': 'Modulus/remainder: MOD(dividend, divisor)',
    'EXP': 'Exponential: EXP(number)',
    'LOG': 'Natural logarithm: LOG(number)',
    'LOG10': 'Base-10 logarithm: LOG10(number)',
    'SIN': 'Sine: SIN(angle_in_radians)',
    'COS': 'Cosine: COS(angle_in_radians)',
    'TAN': 'Tangent: TAN(angle_in_radians)',
    'ASIN': 'Arcsine: ASIN(number)',
    'ACOS': 'Arccosine: ACOS(number)',
    'ATAN': 'Arctangent: ATAN(number)',
    'DEG2RAD': 'Degrees to radians: DEG2RAD(degrees)',
    'RAD2DEG': 'Radians to degrees: RAD2DEG(radians)'
  };

  // Prepare formula components from pay components
  const getFormulaComponents = useCallback(() => {
    return payComponents.map(comp => ({
      id: comp.component_id,
      name: comp.component_name,
      type: comp.component_type,
      displayName: comp.component_name.replace(/\s+/g, '_') // Use underscore for display
    }));
  }, [payComponents]);

  // Memoize fetchAvailableOrders with useCallback to prevent infinite re-renders
  const fetchAvailableOrders = useCallback(async () => {
    try {
      setFetching(true);
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE}/get_salary_details.php`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { emp_id: employee.emp_id }
      });

      if (response.data.success) {
        const existingOrders = response.data.salary_details
          .filter(detail => detail.salary_detail_id !== salaryDetail.salary_detail_id)
          .map(detail => detail.component_order);

        const maxOrder = existingOrders.length > 0 ? Math.max(...existingOrders) : 0;
        const orders = [];
        for (let i = 1; i <= maxOrder + 5; i++) {
          orders.push(i);
        }
        setAvailableOrders(orders);
      }
    } catch (error) {
      console.error('Error fetching available orders:', error);
    } finally {
      setFetching(false);
    }
  }, [API_BASE, employee, salaryDetail]);

  useEffect(() => {
    if (show && salaryDetail && employee) {
      fetchAvailableOrders();
      setFormData({
        salary_detail_id: salaryDetail.salary_detail_id,
        component_id: salaryDetail.component_id,
        component_type: salaryDetail.component_type,
        calculation_type: salaryDetail.calculation_type,
        value: salaryDetail.value,
        component_order: salaryDetail.component_order,
        is_active: salaryDetail.is_active
      });
    }
  }, [show, salaryDetail, employee, fetchAvailableOrders]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData(prev => {
      let newData = {
        ...prev,
        [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
      };

      // When component is selected, auto-set component_type and calculation_type
      if (name === 'component_id' && value) {
        const selectedComponent = payComponents.find(comp => comp.component_id === value);
        if (selectedComponent) {
          newData.component_type = selectedComponent.component_type;
          newData.calculation_type = selectedComponent.calculation_type;
        }
      }

      return newData;
    });

    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }

    // For formula input, show suggestions when typing
    if (name === 'value' && formData.calculation_type === 'Formula') {
      handleFormulaInput(value);
    }
  };

  const handleFormulaInput = (value) => {
    const cursorPos = document.getElementById('value')?.selectionStart || value.length;
    const textBeforeCursor = value.substring(0, cursorPos);

    // Check if user is typing a function or component name
    const lastWordMatch = textBeforeCursor.match(/[A-Z][a-zA-Z0-9_]*$/);

    if (lastWordMatch) {
      const partialName = lastWordMatch[0].toUpperCase();
      const formulaComponents = getFormulaComponents();

      // Check for matching functions first
      const functionMatches = allowedFunctions.filter(func =>
        func.startsWith(partialName)
      ).map(func => ({
        type: 'function',
        name: func,
        displayName: func,
        description: functionDescriptions[func]
      }));

      // Check for matching components
      const componentMatches = formulaComponents.filter(comp =>
        comp.displayName.toUpperCase().startsWith(partialName)
      ).map(comp => ({
        type: 'component',
        ...comp
      }));

      // Combine matches (functions first)
      const allMatches = [...functionMatches, ...componentMatches];

      setFormulaSuggestions(allMatches);
      setShowSuggestions(allMatches.length > 0);
    } else {
      setFormulaSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const insertFormulaText = (text) => {
    const input = document.getElementById('value');
    const currentValue = formData.value;
    const cursorPos = input.selectionStart;

    // Find the partial word at cursor position
    const textBeforeCursor = currentValue.substring(0, cursorPos);
    const lastWordMatch = textBeforeCursor.match(/[A-Z][a-zA-Z0-9_]*$/);

    let newValue;
    if (lastWordMatch) {
      // Replace the partial word with the text
      const startPos = cursorPos - lastWordMatch[0].length;
      newValue = currentValue.substring(0, startPos) + text + currentValue.substring(cursorPos);
    } else {
      // Insert at cursor position
      newValue = currentValue.substring(0, cursorPos) + text + currentValue.substring(cursorPos);
    }

    setFormData(prev => ({ ...prev, value: newValue }));
    setShowSuggestions(false);

    // Focus back to input and set cursor after inserted text
    setTimeout(() => {
      input.focus();
      const newCursorPos = cursorPos + text.length - (lastWordMatch ? lastWordMatch[0].length : 0);
      input.setSelectionRange(newCursorPos, newCursorPos);
    }, 10);
  };

  const handleSuggestionClick = (suggestion) => {
    let textToInsert = suggestion.displayName;

    // If it's a function, add parentheses
    if (suggestion.type === 'function') {
      textToInsert += '()';
    }

    insertFormulaText(textToInsert);
  };

  const insertFunction = (funcName) => {
    const textToInsert = funcName + '()';
    insertFormulaText(textToInsert);
  };

  const insertOperator = (operator) => {
    insertFormulaText(` ${operator} `);
  };

  const insertComparison = (comparison) => {
    insertFormulaText(` ${comparison} `);
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.component_id) {
      errors.component_id = 'Component is required';
    }

    if (!formData.value.trim()) {
      errors.value = 'Value is required';
    } else if (formData.calculation_type === 'Fixed') {
      const numValue = parseFloat(formData.value);
      if (isNaN(numValue)) {
        errors.value = 'Please enter a valid number';
      } else if (numValue < 0) {
        errors.value = 'Amount cannot be negative';
      }
    } else if (formData.calculation_type === 'Variable') {
      const numValue = parseFloat(formData.value);
      if (isNaN(numValue)) {
        errors.value = 'Please enter a valid number';
      } else if (numValue < 0) {
        errors.value = 'Amount cannot be negative';
      }
    } else if (formData.calculation_type === 'Percentage') {
      const numValue = parseFloat(formData.value);
      if (isNaN(numValue)) {
        errors.value = 'Please enter a valid percentage';
      } else if (numValue < 0) {
        errors.value = 'Percentage cannot be negative';
      } else if (numValue > 100) {
        errors.value = 'Percentage cannot exceed 100%';
      }
    } else if (formData.calculation_type === 'Formula') {
      // Basic formula validation
      if (!formData.value.trim()) {
        errors.value = 'Formula cannot be empty';
      }
      // Validate formula syntax
      const formula = formData.value.trim();

      // Check for common syntax errors
      const unbalancedParens = (formula.match(/\(/g) || []).length !== (formula.match(/\)/g) || []).length;
      const invalidChars = formula.match(/[^a-zA-Z0-9_+\-*/\s().,<>=!]/);

      if (unbalancedParens) {
        errors.value = 'Unbalanced parentheses in formula';
      } else if (invalidChars) {
        errors.value = `Invalid character in formula: ${invalidChars[0]}`;
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setError('Please fix the validation errors');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('authToken');
      const salaryDetailData = {
        salary_detail_id: formData.salary_detail_id,
        emp_id: employee.emp_id,
        component_id: formData.component_id,
        component_type: formData.component_type,
        calculation_type: formData.calculation_type,
        value: formData.value,
        component_order: formData.component_order,
        is_active: formData.is_active
      };

      const response = await axios.post(`${API_BASE}/update_salary_detail.php`,
        salaryDetailData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        onSalaryDetailUpdated();
        onHide();
      } else {
        setError(response.data.error || 'Failed to update salary component');
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to update salary component');
      console.error('Error updating salary component:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onHide();
  };

  if (!show) return null;

  const formulaComponents = getFormulaComponents();

  // Filter pay components by type for better grouping
  const earningComponents = payComponents.filter(comp => comp.component_type === 'Earning');
  const deductionComponents = payComponents.filter(comp => comp.component_type === 'Deduction');
  const ctcComponents = payComponents.filter(comp => comp.component_type === 'CTC');
  const generalComponents = payComponents.filter(comp => comp.component_type === 'General');

  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header" style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderBottom: 'none'
          }}>
            <h5 className="modal-title text-white">
              <i className="bi bi-pencil-square me-2"></i>
              Edit Salary Component
            </h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={handleCancel}
              disabled={loading}
            ></button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {/* Information Panel */}
              <div className="alert alert-info mb-4">
                <div className="d-flex">
                  <i className="bi bi-info-circle-fill me-3 fs-4"></i>
                  <div>
                    <h6 className="alert-heading fw-bold">About Salary Components Calculation</h6>
                    <p className="mb-2">You are editing an existing salary component with the following calculation methods:</p>
                    <ul className="mb-2">
                      <li><strong>Fixed:</strong> The entered value remains constant every month (not affected by attendance)</li>
                      <li><strong>Variable:</strong> Value is calculated as: <code>Entered Value × (Effective_Days ÷ Present_Days)</code></li>
                      <li><strong>Percentage:</strong> Value is calculated as: <code>% of Salary Value given in Salary Settings × (Effective_Days ÷ Present_Days)</code>. Present_Days include FH</li>
                      <li><strong>Formula:</strong> Custom calculation using other salary components (e.g., <code>(Basic + DA) × 0.5</code>)</li>
                    </ul>
                    <div className="text-muted small">
                      <strong>Note:</strong> Present_Days is fetched from attendance register, not from Salary Master. Effective_Days represents actual attendance.
                    </div>
                  </div>
                </div>
              </div>

              {error && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  {error}
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setError('')}
                  ></button>
                </div>
              )}

              {/* Employee Info */}
              <div className="card border-0 bg-light mb-4">
                <div className="card-body">
                  <h6 className="fw-bold text-primary mb-3">
                    <i className="bi bi-person me-2"></i>
                    Employee Information
                  </h6>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-2">
                        <small className="text-muted d-block">Employee Name</small>
                        <strong>{employee.first_name} {employee.last_name}</strong>
                      </div>
                      <div className="mb-2">
                        <small className="text-muted d-block">Employee ID</small>
                        <strong>{employee.emp_id}</strong>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-2">
                        <small className="text-muted d-block">Salary Detail ID</small>
                        <strong>{formData.salary_detail_id}</strong>
                      </div>
                      <div className="mb-2">
                        <small className="text-muted d-block">Current Order</small>
                        <strong>{salaryDetail.component_order}</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Form Fields: Pay Component First */}
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="component_id" className="form-label fw-medium">
                      Pay Component <span className="text-danger">*</span>
                    </label>
                    <select
                      className={`form-select ${validationErrors.component_id ? 'is-invalid' : ''}`}
                      id="component_id"
                      name="component_id"
                      value={formData.component_id}
                      onChange={handleChange}
                      required
                      disabled={loading}
                    >
                      <option value="">Select a component</option>
                      {/* Earning Components */}
                      {earningComponents.length > 0 && (
                        <optgroup label="Earning Components">
                          {earningComponents.map(component => (
                            <option key={component.component_id} value={component.component_id}>
                              {component.component_name}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {/* Deduction Components */}
                      {deductionComponents.length > 0 && (
                        <optgroup label="Deduction Components">
                          {deductionComponents.map(component => (
                            <option key={component.component_id} value={component.component_id}>
                              {component.component_name}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {/* CTC Components */}
                      {ctcComponents.length > 0 && (
                        <optgroup label="CTC Components">
                          {ctcComponents.map(component => (
                            <option key={component.component_id} value={component.component_id}>
                              {component.component_name}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {/* General Components */}
                      {generalComponents.length > 0 && (
                        <optgroup label="General Components">
                          {generalComponents.map(component => (
                            <option key={component.component_id} value={component.component_id}>
                              {component.component_name}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </select>
                    {validationErrors.component_id && (
                      <div className="invalid-feedback">{validationErrors.component_id}</div>
                    )}
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="component_order" className="form-label fw-medium">
                      Component Order
                    </label>
                    <select
                      className="form-select"
                      id="component_order"
                      name="component_order"
                      value={formData.component_order}
                      onChange={handleChange}
                      disabled={loading || fetching}
                    >
                      {availableOrders.map(order => (
                        <option key={order} value={order}>
                          Position {order}
                        </option>
                      ))}
                    </select>
                    {fetching && (
                      <div className="form-text">
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Loading available orders...
                      </div>
                    )}
                    <div className="form-text">
                      Order in which this component appears in salary calculation
                    </div>
                  </div>
                </div>
              </div>

              {/* Type and Calculation Row */}
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label fw-medium">Component Type</label>
                    <div className="btn-group w-100" role="group">
                      <input
                        type="radio"
                        className="btn-check"
                        name="component_type"
                        id="type_earning"
                        value="Earning"
                        checked={formData.component_type === 'Earning'}
                        onChange={handleChange}
                        disabled={loading}
                      />
                      <label className="btn btn-outline-success" htmlFor="type_earning">
                        <i className="bi bi-plus-circle me-2"></i>
                        Earning
                      </label>

                      <input
                        type="radio"
                        className="btn-check"
                        name="component_type"
                        id="type_deduction"
                        value="Deduction"
                        checked={formData.component_type === 'Deduction'}
                        onChange={handleChange}
                        disabled={loading}
                      />
                      <label className="btn btn-outline-danger" htmlFor="type_deduction">
                        <i className="bi bi-dash-circle me-2"></i>
                        Deduction
                      </label>

                      <input
                        type="radio"
                        className="btn-check"
                        name="component_type"
                        id="type_ctc"
                        value="CTC"
                        checked={formData.component_type === 'CTC'}
                        onChange={handleChange}
                        disabled={loading}
                      />
                      <label className="btn btn-outline-warning" htmlFor="type_ctc">
                        <i className="bi bi-cash-stack me-2"></i>
                        CTC
                      </label>

                      <input
                        type="radio"
                        className="btn-check"
                        name="component_type"
                        id="type_general"
                        value="General"
                        checked={formData.component_type === 'General'}
                        onChange={handleChange}
                        disabled={loading}
                      />
                      <label className="btn btn-outline-secondary" htmlFor="type_general">
                        <i className="bi bi-gear me-2"></i>
                        General
                      </label>
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label fw-medium">Calculation Type</label>
                    <div className="btn-group w-100" role="group">
                      <input
                        type="radio"
                        className="btn-check"
                        name="calculation_type"
                        id="calc_fixed"
                        value="Fixed"
                        checked={formData.calculation_type === 'Fixed'}
                        onChange={handleChange}
                        disabled={loading}
                      />
                      <label className="btn btn-outline-primary" htmlFor="calc_fixed">
                        Fixed
                      </label>

                      <input
                        type="radio"
                        className="btn-check"
                        name="calculation_type"
                        id="calc_variable"
                        value="Variable"
                        checked={formData.calculation_type === 'Variable'}
                        onChange={handleChange}
                        disabled={loading}
                      />
                      <label className="btn btn-outline-info" htmlFor="calc_variable">
                        Variable
                      </label>

                      <input
                        type="radio"
                        className="btn-check"
                        name="calculation_type"
                        id="calc_percentage"
                        value="Percentage"
                        checked={formData.calculation_type === 'Percentage'}
                        onChange={handleChange}
                        disabled={loading}
                      />
                      <label className="btn btn-outline-success" htmlFor="calc_percentage">
                        Percentage
                      </label>

                      <input
                        type="radio"
                        className="btn-check"
                        name="calculation_type"
                        id="calc_formula"
                        value="Formula"
                        checked={formData.calculation_type === 'Formula'}
                        onChange={handleChange}
                        disabled={loading}
                      />
                      <label className="btn btn-outline-warning" htmlFor="calc_formula">
                        Formula
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-12">
                  <div className="mb-3">
                    <label htmlFor="value" className="form-label fw-medium">
                      Value <span className="text-danger">*</span>
                    </label>

                    {formData.calculation_type === 'Formula' && (
                      <>
                        {/* Formula Tools Section */}
                        <div className="card border mb-3">
                          <div className="card-body p-3">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <h6 className="mb-0">
                                <i className="bi bi-magic me-2"></i>
                                Formula Tools
                              </h6>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => setShowFunctions(!showFunctions)}
                              >
                                {showFunctions ? 'Hide Functions' : 'Show Functions'}
                              </button>
                            </div>

                            {/* Available Components */}
                            <div className="mb-3">
                              <small className="text-muted d-block mb-2">Available Components:</small>
                              <div className="d-flex flex-wrap gap-2">
                                {formulaComponents.map(comp => (
                                  <button
                                    key={comp.id}
                                    type="button"
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => insertFormulaText(comp.displayName)}
                                    disabled={loading}
                                    title={comp.name}
                                  >
                                    <i className="bi bi-tag me-1"></i>
                                    {comp.name}
                                  </button>
                                ))}
                              </div>
                            </div>

                            {/* Operators */}
                            <div className="mb-3">
                              <small className="text-muted d-block mb-2">Operators:</small>
                              <div className="d-flex flex-wrap gap-2">
                                {['+', '-', '*', '/'].map(op => (
                                  <button
                                    key={op}
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => insertOperator(op)}
                                    disabled={loading}
                                  >
                                    {op}
                                  </button>
                                ))}
                              </div>
                            </div>

                            {/* Comparison Operators */}
                            <div className="mb-3">
                              <small className="text-muted d-block mb-2">Comparisons:</small>
                              <div className="d-flex flex-wrap gap-2">
                                {['=', '<>', '!=', '>', '<', '>=', '<='].map(comp => (
                                  <button
                                    key={comp}
                                    type="button"
                                    className="btn btn-sm btn-outline-info"
                                    onClick={() => insertComparison(comp)}
                                    disabled={loading}
                                  >
                                    {comp}
                                  </button>
                                ))}
                              </div>
                            </div>

                            {/* Functions Dropdown */}
                            {showFunctions && (
                              <div className="mb-3">
                                <small className="text-muted d-block mb-2">Available Functions:</small>
                                <div className="row g-2">
                                  {allowedFunctions.map(func => (
                                    <div key={func} className="col-6 col-md-4">
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-warning w-100 text-start"
                                        onClick={() => insertFunction(func)}
                                        disabled={loading}
                                        title={functionDescriptions[func]}
                                      >
                                        <i className="bi bi-code-slash me-1"></i>
                                        {func}
                                        <small className="text-muted d-block small">{functionDescriptions[func]}</small>
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Quick Examples */}
                            <div className="mt-3">
                              <small className="text-muted d-block mb-2">Quick Examples:</small>
                              <div className="d-flex flex-wrap gap-2">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-success"
                                  onClick={() => insertFormulaText('(Basic + DA) * 0.5')}
                                  disabled={loading}
                                >
                                  (Basic + DA) * 0.5
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-success"
                                  onClick={() => insertFormulaText('MAX(Basic, 10000) * 0.4')}
                                  disabled={loading}
                                >
                                  MAX(Basic, 10000) * 0.4
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-success"
                                  onClick={() => insertFormulaText('IF(Basic > 15000, 500, 300)')}
                                  disabled={loading}
                                >
                                  IF(Basic &gt; 15000, 500, 300)
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    <div className="position-relative">
                      <div className="input-group">
                        {formData.calculation_type === 'Fixed' && (
                          <span className="input-group-text">₹</span>
                        )}
                        {formData.calculation_type === 'Variable' && (
                          <span className="input-group-text">₹</span>
                        )}
                        {formData.calculation_type === 'Percentage' && (
                          <span className="input-group-text">%</span>
                        )}
                        <input
                          type={formData.calculation_type === 'Formula' ? 'text' : 'number'}
                          className={`form-control ${validationErrors.value ? 'is-invalid' : ''}`}
                          id="value"
                          name="value"
                          value={formData.value}
                          onChange={handleChange}
                          placeholder={
                            formData.calculation_type === 'Fixed' ? 'Enter fixed amount' :
                              formData.calculation_type === 'Variable' ? 'Enter per day/unit amount' :
                                formData.calculation_type === 'Percentage' ? 'Enter percentage (0-100)' :
                                  'Enter formula or use tools above to build it'
                          }
                          step={formData.calculation_type === 'Percentage' ? '0.01' : '0.01'}
                          min={formData.calculation_type === 'Percentage' ? '0' : '0'}
                          max={formData.calculation_type === 'Percentage' ? '100' : undefined}
                          required
                          disabled={loading}
                          autoComplete="off"
                        />
                      </div>

                      {/* Auto-suggestions Dropdown */}
                      {showSuggestions && formulaSuggestions.length > 0 && (
                        <div className="position-absolute top-100 start-0 end-0 mt-1 z-3">
                          <div className="card border shadow-sm">
                            <div className="card-body p-2">
                              <small className="text-muted d-block mb-2">Suggestions:</small>
                              {formulaSuggestions.map((suggestion, index) => (
                                <button
                                  key={index}
                                  type="button"
                                  className="btn btn-sm w-100 text-start mb-1"
                                  onClick={() => handleSuggestionClick(suggestion)}
                                  style={{
                                    backgroundColor: suggestion.type === 'function' ? '#fff3cd' : '#d1ecf1',
                                    borderColor: suggestion.type === 'function' ? '#ffecb5' : '#bee5eb'
                                  }}
                                >
                                  <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                      <span className="fw-medium">
                                        {suggestion.type === 'function' ? (
                                          <i className="bi bi-code-slash me-1"></i>
                                        ) : (
                                          <i className="bi bi-tag me-1"></i>
                                        )}
                                        {suggestion.name}
                                      </span>
                                      {suggestion.description && (
                                        <small className="d-block text-muted">{suggestion.description}</small>
                                      )}
                                    </div>
                                    <small className="text-muted">{suggestion.displayName}</small>
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {validationErrors.value && (
                      <div className="invalid-feedback d-block">{validationErrors.value}</div>
                    )}
                    <div className="form-text">
                      {formData.calculation_type === 'Fixed' &&
                        'Enter the fixed monthly amount for this component. This amount remains unchanged regardless of attendance.'}
                      {formData.calculation_type === 'Variable' &&
                        'Enter per day/unit amount. Final amount = (Entered Value) × (Effective_Days ÷ Present_Days). Present_Days is fetched from attendance register.'}
                      {formData.calculation_type === 'Percentage' &&
                        'Value = % of Salary Value given in Salary Settings × (Effective_Days ÷ Present_Days). Present_Days include FH.'}
                      {formData.calculation_type === 'Formula' && (
                        <>
                          Use the tools above to build your formula. Available functions: {allowedFunctions.join(', ')}.
                          <br /><strong>Syntax Tips:</strong>
                          <ul className="mb-0">
                            <li>Use component names directly (spaces are replaced with underscores)</li>
                            <li>Functions must be in uppercase (e.g., MIN, MAX, IF)</li>
                            <li>Comparison operators: =, &lt;&gt;, !=, &gt;, &lt;, &gt;=, &lt;=</li>
                            <li>Use parentheses for grouping: (Basic + DA) * 0.5</li>
                          </ul>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-12">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="is_active"
                      name="is_active"
                      checked={formData.is_active === 1}
                      onChange={handleChange}
                      disabled={loading}
                    />
                    <label className="form-check-label" htmlFor="is_active">
                      Active Component
                    </label>
                    <div className="form-text">
                      Inactive components will not be included in salary calculations
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer" style={{
              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
              borderTop: '1px solid rgba(102, 126, 234, 0.1)'
            }}>
              <button
                type="button"
                className="btn btn-light border-secondary-subtle"
                onClick={handleCancel}
                disabled={loading}
              >
                <i className="bi bi-x-circle me-2"></i>
                Cancel
              </button>
              <button
                type="submit"
                className="btn text-white border-0 shadow-sm"
                disabled={loading}
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  fontWeight: '500',
                  letterSpacing: '0.5px',
                  transition: 'all 0.3s',
                  padding: '0.5rem 1.5rem'
                }}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Updating...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle me-2"></i>
                    Update Component
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditSalaryDetail;